import { utils } from '@grammarly/badger-ui'
import type { CitationFormatType, SourceType } from '@grammarly/citation-generator'
import {
  DefiniteArticleType,
  IndefiniteArticleType,
  KeywordSourceTypeRelationship,
  TypeCitationKeywordMetadataFields,
} from '../lib/types/citation-keyword-metadata'
import { TypePageTemplateCitation } from '../lib/types/page-template-citation'

export interface CitationProgrammaticPageContextValue {
  keywordIndefiniteArticle?: IndefiniteArticleType
  keywordDefiniteArticle?: DefiniteArticleType
  keywordDisplayNameSingular?: string
  keywordDisplayNamePlural?: string
  sourceTypeRelationship?: KeywordSourceTypeRelationship
  sourceType?: SourceType
  styleGuide?: CitationFormatType
  automatedLinks?: TypePageTemplateCitation['fields']['automatedLinks']
}

export const [CitationProgrammaticPageContextProvider, useCitationProgrammaticPageContext] =
  utils.createContext<CitationProgrammaticPageContextValue>({
    name: 'CitationProgrammaticPageContext',
  })

export const getCitationProgrammaticPageContextValue = (
  pageSlug: string,
  pageTemplate?: TypePageTemplateCitation
): CitationProgrammaticPageContextValue => {
  if (!pageTemplate) {
    return {}
  }

  const keywordMetadataFields = pageTemplate.fields.keywordMetadata.fields
  const styleGuide = getStyleGuideFromPageSlug(pageSlug)
  const links = pageTemplate.fields.automatedLinks

  return {
    keywordIndefiniteArticle: keywordMetadataFields.indefiniteArticle,
    keywordDefiniteArticle: keywordMetadataFields.definiteArticle,
    keywordDisplayNameSingular: keywordMetadataFields.displayNameSingular,
    keywordDisplayNamePlural: keywordMetadataFields.displayNamePlural,
    styleGuide,
    ...(styleGuide ? getSourceTypeDataByStyleGuide(keywordMetadataFields, styleGuide) : {}),
    automatedLinks: links,
  }
}

const getStyleGuideFromPageSlug = (pageSlug: string): CitationFormatType | undefined => {
  const citationsPrefix = 'citations/'
  const pageSlugWithoutCitationsPrefix = pageSlug.substring(pageSlug.indexOf(citationsPrefix) + citationsPrefix.length)
  const styleGuideInPageSlug = pageSlugWithoutCitationsPrefix.substring(0, pageSlugWithoutCitationsPrefix.indexOf('/'))

  switch (styleGuideInPageSlug) {
    case 'mla':
      return 'MLA'
    case 'apa':
      return 'APA'
    case 'chicago':
      return 'Chicago'
  }
}

const getSourceTypeDataByStyleGuide = (
  keywordMetadataFields: TypeCitationKeywordMetadataFields,
  styleGuide: CitationFormatType
) => {
  switch (styleGuide) {
    case 'MLA':
      return {
        sourceTypeRelationship: keywordMetadataFields.mlaSourceTypeRelationship,
        sourceType: keywordMetadataFields.mlaSourceType,
      }
    case 'APA':
      return {
        sourceTypeRelationship: keywordMetadataFields.apaSourceTypeRelationship,
        sourceType: keywordMetadataFields.apaSourceType,
      }
    case 'Chicago':
      return {
        sourceTypeRelationship: keywordMetadataFields.chicagoSourceTypeRelationship,
        sourceType: keywordMetadataFields.chicagoSourceType,
      }
  }
}
